import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send message');
      }

      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="contact-page" data-testid="contact-page">
        <div className="contact-container">
          <div className="contact-success">
            <div className="success-icon">
              <Check className="h-12 w-12" />
            </div>
            <h1>Message Sent!</h1>
            <p>Thanks for reaching out. The Washed Away team will get back to you soon.</p>
            <Button onClick={() => navigate('/')} className="contact-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page" data-testid="contact-page">
      <div className="contact-container">
        <header className="contact-header">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="back-button"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </Button>
        </header>

        <div className="contact-content">
          <div className="contact-info">
            <img 
              src="https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png" 
              alt="Washed Away"
              className="contact-logo"
            />
            <h1>Get in Touch</h1>
            <p>Have a question, feedback, or want to share your story? We'd love to hear from you.</p>
            
            <div className="contact-details">
              <div className="contact-detail">
                <span className="detail-label">General Inquiries</span>
                <a href="mailto:hello@washedaway.au" className="detail-value">hello@washedaway.au</a>
              </div>
              <div className="contact-detail">
                <span className="detail-label">Streaming Support</span>
                <a href="mailto:support@washedaway.au" className="detail-value">support@washedaway.au</a>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="What's this about?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your message..."
                rows={6}
                required
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="contact-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
